import styles from "./Events.module.scss";
import { FC } from "react";
import { withStockStamp } from "@/components/stock-stamp-hoc/StockStampHOC";
import { classNames, formatDate } from "@/lib/utils";
import { Box, Divider, Tooltip, Typography } from "@mui/material";
import dayjs from "dayjs";
import { dividerState } from "@/lib/store";
import { useAtomValue } from "jotai";

interface PropsTypes {
    eventsUpcoming: [number, string, string][];
    eventsPrevious: [number, string, string][];
    overviewPage?: boolean;
}

const Events: FC<PropsTypes> = ({ eventsUpcoming, eventsPrevious, overviewPage }) => {
    const dividerColor = useAtomValue(dividerState);

    const handleDate = (date: number, type: string) => {
        const dateDifference = dayjs().diff(date, "day");

        if (Math.abs(dateDifference) < 2) {
            switch (dateDifference) {
                case 0:
                    return "Today";
                case 1:
                    return "Yesterday";
                case -1:
                    return "Tomorrow";
                default:
                    return formatDate(dayjs(date));
            }
        } else {
            return formatDate(dayjs(date));
        }
    };

    return (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-around", alignItems: "center", overflow: "auto" }} className={classNames(styles.Events, styles[overviewPage && "overview-page"])}>
            {eventsUpcoming.length || eventsPrevious.length ? (
                <>
                    <Box sx={{ width: "100%" }}>
                        {eventsUpcoming?.length ? (
                            <>
                                <Typography
                                    sx={{
                                        textTransform: "initial",
                                        textAlign: "center",
                                        fontSize: "12px",
                                        "@media screen and (min-width: 1280px)": {
                                            fontSize: "10px",
                                        },
                                        "@media screen and (min-width: 1920px)": {
                                            fontSize: "12px",
                                        },
                                        fontWeight: "700",
                                    }}
                                >
                                    Upcoming Events
                                </Typography>
                                {eventsUpcoming.map((event, index) => (
                                    <Tooltip key={`upcoming - ${event?.[1]} ${index}`} title={event?.[2]} placement="top" arrow>
                                        <Box className={styles.event}>
                                            <Typography
                                                sx={{
                                                    fontWeight: "600",
                                                    textTransform: "initial",
                                                    fontSize: "10px",
                                                    textAlign: "right",
                                                    "@media screen and (min-width: 1280px)": {
                                                        fontSize: "8px",
                                                    },
                                                    "@media screen and (min-width: 1920px)": {
                                                        fontSize: "10px",
                                                    },
                                                }}
                                            >
                                                {handleDate(event?.[0], "upcoming")}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    textTransform: "initial",
                                                    textAlign: "center",
                                                    width: !event?.[0] ? "100%" : "fit-content",
                                                    fontSize: "10px",
                                                    "@media screen and (min-width: 1280px)": {
                                                        fontSize: "8px",
                                                    },
                                                    "@media screen and (min-width: 1920px)": {
                                                        fontSize: "10px",
                                                    },
                                                }}
                                            >
                                                {event?.[1]}
                                            </Typography>
                                        </Box>
                                    </Tooltip>
                                ))}
                            </>
                        ) : (
                            <Typography
                                sx={{
                                    textTransform: "initial",
                                    color: "grey",
                                    fontSize: "10px",
                                    "@media screen and (min-width: 1280px)": {
                                        fontSize: "8px",
                                    },
                                    "@media screen and (min-width: 1920px)": {
                                        fontSize: "10px",
                                    },
                                    lineHeight: 1,
                                }}
                            >
                                No upcoming events
                            </Typography>
                        )}
                    </Box>
                    <Divider flexItem sx={{ my: "10px", backgroundColor: dividerColor }} />
                    <Box sx={{ width: "100%" }}>
                        {eventsPrevious?.length ? (
                            <>
                                <Typography
                                    sx={{
                                        textTransform: "initial",
                                        textAlign: "center",
                                        fontSize: "12px",
                                        "@media screen and (min-width: 1280px)": {
                                            fontSize: "10px",
                                        },
                                        "@media screen and (min-width: 1920px)": {
                                            fontSize: "12px",
                                        },
                                        fontWeight: "700",
                                    }}
                                >
                                    Recent Events
                                </Typography>
                                {eventsPrevious.map((event, index) => (
                                    <Tooltip key={`previous - ${event?.[1]} ${index}`} title={event?.[2]} placement="top" arrow>
                                        <Box className={styles.event}>
                                            <Typography
                                                sx={{
                                                    fontWeight: "600",
                                                    textTransform: "initial",
                                                    fontSize: "10px",
                                                    textAlign: "right",
                                                    "@media screen and (min-width: 1280px)": {
                                                        fontSize: "8px",
                                                    },
                                                    "@media screen and (min-width: 1920px)": {
                                                        fontSize: "10px",
                                                    },
                                                }}
                                            >
                                                {handleDate(event?.[0], "previous")}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    textTransform: "initial",
                                                    textAlign: "center",
                                                    width: !event?.[0] ? "100%" : "fit-content",
                                                    fontSize: "10px",
                                                    "@media screen and (min-width: 1280px)": {
                                                        fontSize: "8px",
                                                    },
                                                    "@media screen and (min-width: 1920px)": {
                                                        fontSize: "10px",
                                                    },
                                                }}
                                            >
                                                {event?.[1]}
                                            </Typography>
                                        </Box>
                                    </Tooltip>
                                ))}
                            </>
                        ) : (
                            <Typography
                                sx={{
                                    textTransform: "initial",
                                    color: "grey",
                                    fontSize: "10px",
                                    "@media screen and (min-width: 1280px)": {
                                        fontSize: "8px",
                                    },
                                    "@media screen and (min-width: 1920px)": {
                                        fontSize: "10px",
                                    },
                                    lineHeight: 1,
                                }}
                            >
                                No recent events
                            </Typography>
                        )}
                    </Box>
                </>
            ) : (
                <Typography
                    sx={{
                        textTransform: "initial",
                        color: "grey",
                        fontSize: "10px",
                        "@media screen and (min-width: 1280px)": {
                            fontSize: "8px",
                        },
                        "@media screen and (min-width: 1920px)": {
                            fontSize: "10px",
                        },
                        lineHeight: 1,
                    }}
                >
                    No recent or upcoming events
                </Typography>
            )}
        </Box>
    );
};

export default withStockStamp(Events, "eventsUpcoming", { title: "Events" }, "Events");
